/**
 * FontWarmupService - Preload all fonts on app initialization
 * 
 * Two-phase warmup:
 * 1. FontResolver cache warmup (loads font metadata from API)
 * 2. FontDownloadService preload (downloads font bytes for HarfBuzz)
 * 
 * This ensures:
 * - Font selection in toolbar is instant (cache hit)
 * - WASM typesetting doesn't wait for font downloads
 * - No layout timing issues or race conditions
 */

import { fontResolver } from './FontResolver';
import { getEnabledFontFamilies, type FontFamily } from './FontAPI';
import { FontDownloadService } from '../rendering/fonts/FontDownloadService';

export interface WarmupProgress {
  phase: 'resolver' | 'download' | 'complete';
  progress: number; // 0-100
  currentFont?: string;
  totalFonts: number;
  loadedFonts: number;
}

export type WarmupCallback = (progress: WarmupProgress) => void;

class FontWarmupService {
  private isWarmedUp: boolean = false;
  private warmupPromise: Promise<void> | null = null;
  
  /**
   * Warm up all font systems
   * Should be called once on app mount
   * 
   * @param onProgress - Optional callback for progress updates
   */
  async warmup(onProgress?: WarmupCallback): Promise<void> {
    // Prevent duplicate warmup
    if (this.warmupPromise) {
      console.log('[FontWarmupService] Warmup already in progress, awaiting...');
      return this.warmupPromise;
    }
    
    if (this.isWarmedUp) {
      console.log('[FontWarmupService] Already warmed up');
      return;
    }
    
    console.log('[FontWarmupService] 🔥 Starting font warmup...');
    const startTime = performance.now();
    
    this.warmupPromise = (async () => {
      try {
        // ============================================================
        // PHASE 1: Warm FontResolver cache (metadata only)
        // ============================================================
        console.log('[FontWarmupService] Phase 1: Loading font metadata...');
        onProgress?.({
          phase: 'resolver',
          progress: 0,
          totalFonts: 0,
          loadedFonts: 0
        });
        
        await fontResolver.warmCache();
        
        const stats = fontResolver.getCacheStats();
        console.log(`[FontWarmupService] ✅ Phase 1 complete: ${stats.totalEntries} fonts cached`);
        
        // ============================================================
        // Phase 2/3 Disabled by default: on-demand loading only
        // ============================================================
        console.log('[FontWarmupService] Phase 2/3 skipped (on-demand font loading enabled)');
        
        // ============================================================
        // COMPLETE
        // ============================================================
        const duration = performance.now() - startTime;
        console.log(`[FontWarmupService] ✅ Warmup complete in ${duration.toFixed(0)}ms`);
        
        onProgress?.({
          phase: 'complete',
          progress: 100,
          totalFonts: 0,
          loadedFonts: 0
        });
        
        this.isWarmedUp = true;
        
        // Log cache statistics
        const cacheStats = await FontDownloadService.getCacheStats();
        console.log('[FontWarmupService] Font cache statistics:', {
          resolverCached: stats.totalEntries,
          bytesCached: cacheStats.count,
          totalSizeMB: (cacheStats.totalSize / 1024 / 1024).toFixed(2)
        });
        
      } catch (error) {
        console.error('[FontWarmupService] ❌ Warmup failed:', error);
        // Don't throw - allow app to continue with fallback fonts
        this.isWarmedUp = false;
      } finally {
        this.warmupPromise = null;
      }
    })();
    
    return this.warmupPromise;
  }
  
  /**
   * Check if fonts are warmed up
   */
  isReady(): boolean {
    return this.isWarmedUp;
  }
  
  /**
   * Reset warmup state (for testing or after font updates)
   */
  reset(): void {
    console.log('[FontWarmupService] Resetting warmup state...');
    this.isWarmedUp = false;
    this.warmupPromise = null;
  }
  
  /**
   * Re-warm fonts after Font Manager changes
   * (e.g., after uploading new fonts or enabling/disabling fonts)
   */
  async refresh(): Promise<void> {
    console.log('[FontWarmupService] Refreshing fonts...');
    this.reset();
    await fontResolver.refreshCache();
    await this.warmup();
  }
}

// Singleton instance
export const fontWarmupService = new FontWarmupService();

// Export class for testing
export { FontWarmupService };

